<template>
    <div v-if="showCategories" ref="picker" class="plan-category-picker" :class="{ 'plan-category-picker--large-toolbar': largeToolbar }">
        <div
            v-if="usePillLayout"
            class="plan-category-toggle-row"
            role="group"
            aria-labelledby="plan-category-label"
        >
            <span id="plan-category-label" class="plan-category-field-label"><strong>Search for:</strong></span>
            <div class="plan-category-buttons">
                <button
                    v-for="category in availablePlanCategories"
                    :key="category.id"
                    type="button"
                    class="btn plan-category-button"
                    :class="isCategoryActive(category) ? 'plan-category-button--selected' : 'plan-category-button--unselected'"
                    @click="selectCategory(String(category.id))"
                >
                    {{ category.description }}
                </button>
            </div>
        </div>
        <div v-else class="plan-category-select-row">
            <label class="plan-category-field-label" for="plan-category-select"><strong>Search for:</strong></label>
            <select
                id="plan-category-select"
                class="form-select plan-category-select"
                :value="selectValue"
                @change="selectCategory($event.target.value)"
            >
                <option value="">Choose plan type…</option>
                <option v-for="category in availablePlanCategories" :key="category.id" :value="String(category.id)">
                    {{ category.description }}
                </option>
            </select>
        </div>

        <!-- Hidden nowrap measure row: detect when pills would wrap on desktop -->
        <div
            v-if="shouldMeasurePills"
            ref="measureRow"
            class="plan-category-toggle-row plan-category-measure"
            aria-hidden="true"
        >
            <span class="plan-category-field-label"><strong>Search for:</strong></span>
            <div class="plan-category-buttons">
                <button
                    v-for="category in availablePlanCategories"
                    :key="'measure-' + category.id"
                    type="button"
                    tabindex="-1"
                    disabled
                    class="btn plan-category-button plan-category-button--unselected"
                >
                    {{ category.description }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import {api} from "../../../global/framework/api.js";
    import {common} from "../../../global/common.js";

    /** Above this count, always show a dropdown instead of toggle buttons (all 7 categories). */
    const MAX_TOGGLE_BUTTONS = 6;
    const DESKTOP_MEDIA_QUERY = '(min-width: 992px)';

    export default {
        name: "PlanCategoryButtons",
        props: {
            consumerId: {type: Number, default: null},
            consultantId: {type: Number, default: null},
            companyId: {type: Number, default: null},
            token: {type: String, default: null},
            /** When true (MSR search bar), scale up label, pills, and dropdown for Bootstrap 3 layouts */
            largeToolbar: {type: Boolean, default: false}
        },
        data() {
            return {
                availablePlanCategories: [],
                ready: false,
                isDesktop: typeof window !== 'undefined' && window.matchMedia
                    ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
                    : true,
                pillsOverflowDesktop: false,
                desktopMediaQuery: null,
                resizeObserver: null
            };
        },
        computed: {
            showCategories() {
                return this.ready && this.availablePlanCategories.length > 0;
            },
            activeCategoryId() {
                const rawPlanCategoryId = this.$route.query.planCategoryId;
                return typeof rawPlanCategoryId === 'undefined'
                    || rawPlanCategoryId === null
                    || rawPlanCategoryId === ''
                    ? null
                    : Number(rawPlanCategoryId);
            },
            selectValue() {
                return this.activeCategoryId !== null ? String(this.activeCategoryId) : '';
            },
            shouldMeasurePills() {
                return this.availablePlanCategories.length > 0
                    && this.availablePlanCategories.length <= MAX_TOGGLE_BUTTONS;
            },
            usePillLayout() {
                if (this.availablePlanCategories.length === 0) {
                    return false;
                }
                if (this.availablePlanCategories.length > MAX_TOGGLE_BUTTONS) {
                    return false;
                }
                // Mobile may wrap; only switch to dropdown for overflow on desktop.
                if (this.isDesktop && this.pillsOverflowDesktop) {
                    return false;
                }
                return true;
            }
        },
        async mounted() {
            if (this.companyId != null && this.token) {
                let url = `ProviderSearch/${this.companyId}/GetPlanCategories`;
                url = common.appendUrlParameter(url, 'token', this.token);
                this.availablePlanCategories = await $.ajax({type: 'GET', url});
            } else if (this.consultantId != null && this.consumerId != null) {
                this.availablePlanCategories = await api.getDataWithTwoIds(
                    'Psr', this.consultantId, 'AvailableCategoriesForConsumer', this.consumerId
                );
            } else if (this.consumerId != null) {
                this.availablePlanCategories = await api.getData(
                    'Consumer', this.consumerId, 'AvailableCategoriesForConsumer'
                );
            } else {
                this.availablePlanCategories = [];
            }
            this.ready = true;
            if (this.availablePlanCategories.length === 1 && this.activeCategoryId === null) {
                this.selectCategory(this.availablePlanCategories[0].id);
            }
            this.setupOverflowWatchers();
            this.$nextTick(() => {
                this.observeOverflowTarget();
                this.updatePillsOverflow();
            });
        },
        beforeDestroy() {
            this.teardownOverflowWatchers();
        },
        methods: {
            selectCategory(categoryId) {
                if (categoryId === '') {
                    return;
                }
                const currentPlanCategoryId = this.$route.query.planCategoryId;
                if (String(currentPlanCategoryId) === String(categoryId)) {
                    return;
                }
                const nav = this.$router.push({
                    path: this.$route.path,
                    query: {
                        ...this.$route.query,
                        planCategoryId: categoryId
                    },
                    hash: this.$route.hash
                });
                if (nav && typeof nav.catch === 'function') {
                    nav.catch(() => {});
                }
            },
            isCategoryActive(category) {
                return this.activeCategoryId !== null
                    && Number(category.id) === Number(this.activeCategoryId);
            },
            setupOverflowWatchers() {
                if (typeof window === 'undefined') {
                    return;
                }
                if (window.matchMedia) {
                    this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
                    this.isDesktop = this.desktopMediaQuery.matches;
                    this.onDesktopMediaChange = (event) => {
                        this.isDesktop = event.matches;
                        this.$nextTick(() => this.updatePillsOverflow());
                    };
                    if (this.desktopMediaQuery.addEventListener) {
                        this.desktopMediaQuery.addEventListener('change', this.onDesktopMediaChange);
                    } else if (this.desktopMediaQuery.addListener) {
                        this.desktopMediaQuery.addListener(this.onDesktopMediaChange);
                    }
                }
                if (typeof ResizeObserver !== 'undefined') {
                    this.resizeObserver = new ResizeObserver(() => {
                        this.updatePillsOverflow();
                    });
                } else {
                    this.onWindowResize = () => this.updatePillsOverflow();
                    window.addEventListener('resize', this.onWindowResize);
                }
            },
            observeOverflowTarget() {
                if (!this.resizeObserver || !this.$el || !this.$el.parentElement) {
                    return;
                }
                this.resizeObserver.disconnect();
                this.resizeObserver.observe(this.$el.parentElement);
            },
            teardownOverflowWatchers() {
                if (this.desktopMediaQuery && this.onDesktopMediaChange) {
                    if (this.desktopMediaQuery.removeEventListener) {
                        this.desktopMediaQuery.removeEventListener('change', this.onDesktopMediaChange);
                    } else if (this.desktopMediaQuery.removeListener) {
                        this.desktopMediaQuery.removeListener(this.onDesktopMediaChange);
                    }
                }
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                    this.resizeObserver = null;
                }
                if (this.onWindowResize) {
                    window.removeEventListener('resize', this.onWindowResize);
                    this.onWindowResize = null;
                }
            },
            updatePillsOverflow() {
                if (!this.shouldMeasurePills || !this.isDesktop) {
                    this.pillsOverflowDesktop = false;
                    return;
                }
                const measureRow = this.$refs.measureRow;
                const availableEl = this.$el && this.$el.parentElement
                    ? this.$el.parentElement
                    : this.$refs.picker;
                if (!measureRow || !availableEl) {
                    return;
                }
                const requiredWidth = measureRow.scrollWidth;
                const availableWidth = availableEl.clientWidth;
                this.pillsOverflowDesktop = requiredWidth > availableWidth + 1;
            }
        },
        watch: {
            showCategories(isShown) {
                if (isShown) {
                    this.$nextTick(() => {
                        this.observeOverflowTarget();
                        this.updatePillsOverflow();
                    });
                }
            },
            availablePlanCategories() {
                this.$nextTick(() => this.updatePillsOverflow());
            }
        }
    };
</script>

<style scoped>
    .plan-category-picker {
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
        max-width: 100%;
    }

    .plan-category-toggle-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.65rem 1rem;
    }

    .plan-category-select-row {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 0.65rem 1rem;
        width: 100%;
        max-width: 100%;
    }

    .plan-category-field-label {
        display: inline-flex;
        align-items: center;
        margin: 0;
        margin-bottom: 0;
        font-size: 1rem;
        line-height: 1.2;
        color: #3e3f40;
        white-space: nowrap;
        flex: 0 0 auto;
    }

    .plan-category-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
    }

    .plan-category-button {
        border-radius: 999px;
        padding: 0.45rem 1rem;
        font-size: 0.9rem;
        font-weight: 600;
        line-height: 1.2;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .plan-category-button--unselected {
        background-color: white;
        color: var(--bs-success, #198754) !important;
    }

    .plan-category-button--unselected:hover {
        background: rgba(25, 135, 84, 0.08);
    }

    .plan-category-button--selected {
        background: var(--bs-success, #198754);
        color: #fff !important;
        border-color: var(--bs-success, #198754);
    }

    @keyframes plan-category-selected-glow {
        0%,
        100% {
            box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.22);
        }

        50% {
            box-shadow: 0 0 14px 5px rgba(25, 135, 84, 0.42);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .plan-category-button--selected {
            animation: none;
            box-shadow: 0 0 10px 3px rgba(25, 135, 84, 0.32);
        }
    }

    .plan-category-select {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
        max-width: min(100%, 420px);
        min-height: 44px;
        padding: 0.5rem 2.25rem 0.5rem 0.75rem;
        font-size: 0.95rem;
        border-radius: 8px;
        border: 1px solid #ced4da;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .plan-category-select:focus {
        border-color: #86b7fe;
        box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.2);
        outline: 0;
    }

    .plan-category-measure {
        position: absolute;
        left: 0;
        top: 0;
        width: max-content;
        max-width: none;
        visibility: hidden;
        pointer-events: none;
        z-index: -1;
        flex-wrap: nowrap;
    }

    .plan-category-measure .plan-category-buttons {
        flex-wrap: nowrap;
    }

    @media (max-width: 991.98px) {
        .plan-category-select-row {
            flex-wrap: wrap;
        }
    }

    .plan-category-picker--large-toolbar .plan-category-toggle-row,
    .plan-category-picker--large-toolbar .plan-category-select-row {
        gap: 1rem 1.5rem;
    }

    .plan-category-picker--large-toolbar .plan-category-field-label {
        font-size: 1.45rem;
        line-height: 1.3;
    }

    .plan-category-picker--large-toolbar .plan-category-buttons {
        gap: 0.75rem;
    }

    .plan-category-picker--large-toolbar .plan-category-button {
        padding: 0.85rem 1.75rem;
        font-size: 1.2rem;
        line-height: 1.25;
        min-height: 3rem;
        border-width: 2px;
    }

    .plan-category-picker--large-toolbar .plan-category-select {
        min-height: 3.25rem;
        padding: 0.65rem 2.5rem 0.65rem 1rem;
        font-size: 1.2rem;
        border-width: 2px;
    }
</style>
